import { useCallback, useEffect, useMemo, useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { todayISO, fmtLongDate } from "@/lib/format";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { SecureGate, SecureLockButton } from "@/components/SecureGate";
import { downloadBlob } from "@/lib/pdf";
import { QrSitesPanel } from "@/components/QrSitesPanel";

function ExportCard() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/punches/export", { params: { month }, responseType: "blob" });
      downloadBlob(data, `presenze-${month}.xlsx`);
    } catch (e) {
      toast.error("Download non riuscito");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3 rounded-2xl border bg-white p-5" data-testid="punch-export-card">
      <p className="font-display text-lg font-semibold">Export presenze</p>
      <p className="text-xs text-slate-500">File Excel del mese con riepilogo ore per dipendente e dettaglio entrate/uscite.</p>
      <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="punch-export-month" />
      <Button onClick={download} disabled={busy} className="w-full rounded-full" data-testid="punch-export-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Scarica Excel
      </Button>
    </div>
  );
}

const hhmm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });


function summarize(punches) {
  const rows = {};
  punches.forEach((p) => {
    const k = `${p.date}|${p.technician_id}`;
    const r = (rows[k] ||= { date: p.date, name: p.technician_name, items: [], minutes: 0, open: null });
    r.items.push(p);
    if (p.type === "entrata") r.open = p.at;
    else if (r.open) { r.minutes += (new Date(p.at) - new Date(r.open)) / 60000; r.open = null; }
  });
  return Object.values(rows);
}

function AutoClosed({ onChange }) {
  const [items, setItems] = useState([]);
  const [times, setTimes] = useState({});
  const load = useCallback(() => api.get("/admin/punches/review").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const fix = async (p) => {
    try {
      await api.post(`/admin/punches/${p.id}/fix`, { time: times[p.id] || "17:00" });
      toast.success("Uscita corretta");
      load();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!items.length) return null;
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50/60" data-testid="punch-review">
      <p className="border-b border-red-200 px-5 py-3 font-display font-semibold">Uscite chiuse in automatico da correggere ({items.length})</p>
      <div className="divide-y divide-red-200">
        {items.map((p, i) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`punch-review-${i}`}>
            <p className="font-semibold">{p.technician_name} · <span className="capitalize">{fmtLongDate(p.date)}</span></p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600">Uscita reale</span>
              <Input type="time" className="w-28" value={times[p.id] || "17:00"} onChange={(e) => setTimes((t) => ({ ...t, [p.id]: e.target.value }))} data-testid={`punch-review-time-${i}`} />
              <Button size="sm" className="rounded-full" onClick={() => fix(p)} data-testid={`punch-review-save-${i}`}>Salva</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PunchRequests({ onChange }) {
  const [reqs, setReqs] = useState([]);
  const load = useCallback(() => api.get("/admin/punch-requests").then((r) => setReqs(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const decide = async (r, approve) => {
    try {
      await api.post(`/admin/punch-requests/${r.id}/decision`, { approve });
      toast.success(approve ? "Entrata registrata" : "Richiesta rifiutata");
      load();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!reqs.length) return null;
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/60" data-testid="punch-requests">
      <p className="border-b border-amber-200 px-5 py-3 font-display font-semibold">Entrate dimenticate da approvare ({reqs.length})</p>
      <div className="divide-y divide-amber-200">
        {reqs.map((r, i) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`punch-request-${i}`}>
            <div>
              <p className="font-semibold">{r.technician_name} · <span className="capitalize">{fmtLongDate(r.date)}</span> alle {r.time}</p>
              <p className="text-xs text-slate-600">{r.note}</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => decide(r, false)} data-testid={`punch-request-reject-${i}`}>Rifiuta</Button>
              <Button size="sm" className="rounded-full bg-[#61a229] hover:bg-[#528a22]" onClick={() => decide(r, true)} data-testid={`punch-request-approve-${i}`}>Approva</Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PunchTable({ version }) {
  const [start, setStart] = useState(todayISO());
  const [end, setEnd] = useState(todayISO());
  const [punches, setPunches] = useState([]);
  const load = useCallback(() => api.get("/admin/punches", { params: { start, end } }).then((r) => setPunches(r.data)), [start, end]);
  useEffect(() => { load(); }, [load, version]);
  const rows = useMemo(() => summarize(punches), [punches]);
  return (
    <div className="rounded-2xl border bg-white" data-testid="punch-table">
      <div className="flex flex-wrap items-center gap-2 border-b p-4">
        <Input type="date" value={start} onChange={(e) => e.target.value && setStart(e.target.value)} className="w-40" data-testid="punch-start" />
        <span className="text-slate-400">–</span>
        <Input type="date" value={end} onChange={(e) => e.target.value && setEnd(e.target.value)} className="w-40" data-testid="punch-end" />
      </div>
      <div className="divide-y">
        {rows.map((r, i) => (
          <div key={i} className="grid gap-1 px-5 py-3 sm:grid-cols-[180px_1fr_auto] sm:items-center" data-testid={`punch-row-${i}`}>
            <div><p className="font-semibold">{r.name}</p><p className="text-xs capitalize text-slate-500">{fmtLongDate(r.date)}</p></div>
            <div className="flex flex-wrap gap-1.5">
              {r.items.map((p) => (
                <a key={p.id} href={p.lat ? `https://www.google.com/maps?q=${p.lat},${p.lng}` : undefined} target="_blank" rel="noreferrer" title={p.manual ? `Approvata dall'admin: ${p.note || ""}` : `${p.distance_m} m da ${p.site_name || "sede"}`}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold ${p.type === "entrata" ? "bg-[#a2ca6b]/25 text-[#3d6b16]" : "bg-slate-100 text-slate-700"}`}>
                  {p.type === "entrata" ? "Entrata" : "Uscita"} {hhmm(p.at)}{p.manual ? " · manuale" : ""}{p.auto ? (p.needs_review ? " · automatica, da correggere" : " · corretta") : ""}
                </a>
              ))}
              {r.open && <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">In servizio</span>}
            </div>
            <p className="font-mono text-sm" data-testid={`punch-hours-${i}`}>{Math.floor(r.minutes / 60)}h {String(Math.round(r.minutes % 60)).padStart(2, "0")}m</p>
          </div>
        ))}
        {!rows.length && <p className="p-6 text-center text-sm text-slate-500" data-testid="punch-empty">Nessuna timbratura nel periodo.</p>}
      </div>
    </div>
  );
}

export default function AdminPunches() {
  const [version, setVersion] = useState(0);
  return (
    <div>
      <PageHeader eyebrow="Presenze con QR Code" title="Timbrature"><SecureLockButton /></PageHeader>
      <SecureGate>
        <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
          <div className="space-y-6">
            <AutoClosed onChange={() => setVersion((v) => v + 1)} />
            <PunchRequests onChange={() => setVersion((v) => v + 1)} />
            <PunchTable version={version} />
          </div>
          <div className="space-y-6"><ExportCard /><QrSitesPanel /></div>
        </div>
      </SecureGate>
    </div>
  );
}
